import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  Chip,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { motion, AnimatePresence } from 'framer-motion';

const MotionListItem = motion(ListItem);

// Mock news data for development
const MOCK_NEWS = [
  {
    id: 1,
    title: "Bitcoin Surges Past $50K as Institutional Interest Grows",
    source: "CryptoNews",
    sentiment: "bullish",
    timestamp: new Date().toLocaleTimeString(),
    url: "#"
  },
  {
    id: 2,
    title: "Major DeFi Protocol Announces Token Launch",
    source: "DeFiWorld",
    sentiment: "bullish",
    timestamp: new Date().toLocaleTimeString(),
    url: "#"
  },
  {
    id: 3,
    title: "Regulatory Concerns Impact Crypto Markets",
    source: "CryptoDaily",
    sentiment: "bearish",
    timestamp: new Date().toLocaleTimeString(),
    url: "#"
  },
  {
    id: 4,
    title: "New NFT Collection Sells Out in Minutes",
    source: "NFTNews",
    sentiment: "bullish",
    timestamp: new Date().toLocaleTimeString(),
    url: "#"
  },
  {
    id: 5,
    title: "Major Exchange Faces Technical Issues",
    source: "CryptoAlert",
    sentiment: "bearish",
    timestamp: new Date().toLocaleTimeString(),
    url: "#"
  }
];

function NewsEvents({ darkMode }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = async () => {
    try {
      // In development, use mock data
      if (process.env.NODE_ENV === 'development') {
        setNews(MOCK_NEWS);
        setLoading(false);
        return;
      }

      // In production, try to fetch real data
      const response = await fetch('/api/v1/posts/?auth_token=da078a1d1a170f665561fea727ce00110d41bb77&kind=news&filter=hot&public=true');
      const data = await response.json();
      
      const processedNews = data.results
        ?.slice(0, 5)
        .map(item => ({
          id: item.id,
          title: item.title,
          source: item.source.title,
          sentiment: item.votes?.positive > item.votes?.negative ? 'bullish' : 'bearish',
          timestamp: new Date(item.published_at).toLocaleTimeString(),
          url: item.url
        })) || [];
      
      setNews(processedNews);
    } catch (err) {
      console.error('Error fetching news:', err);
      // Fallback to mock data if API fails
      setNews(MOCK_NEWS);
      setError('Using offline news data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    // Fetch news every 5 minutes
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  const getSentimentColor = (sentiment) => {
    return sentiment === 'bullish' ? '#4caf50' : '#f44336';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mt: 3,
        background: darkMode 
          ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1))'
          : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.95))',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        border: '1px solid rgba(255, 255, 255, 0.18)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="div" sx={{ 
          fontWeight: 'bold',
          background: darkMode ? 
            'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)' : 
            'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          📰 Crypto News
        </Typography>
        <IconButton onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
          <List>
            <AnimatePresence>
              {news.map((item, index) => (
                <MotionListItem
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                  component={motion.div}
                  whileHover={{ scale: 1.02 }}
                  sx={{ 
                    mb: 1,
                    borderRadius: 2,
                    background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {item.title}
                        </Typography>
                        <Chip
                          label={item.sentiment}
                          size="small"
                          sx={{
                            backgroundColor: getSentimentColor(item.sentiment),
                            color: 'white',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography variant="caption" color="textSecondary">
                          {item.source}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {item.timestamp}
                        </Typography>
                      </Box>
                    }
                  />
                </MotionListItem>
              ))}
            </AnimatePresence>
          </List>
        )}
      </Collapse>
    </Paper>
  );
}

export default NewsEvents; 